import { getFileIcon } from '@sohanscript/lib/code/get-file-icon';
import { parseAttributes, parseLineRanges } from './attributes';

export type FenceMeta = {
	filename: string;
	icon: string;
	name: string;
	highlight: string;
	language: string;
};

/**
 * Reads the fence meta contract: `filename="…" icon="…" name="…"
 * highlight="2,4-6"`, plus the parser's own `title=` / `file=` / `lines=` /
 * `{2,4-6}` forms so metadata keeps working if the site adopts them.
 *
 * TanStack Markdown hands the raw meta string to `pre` as `data-meta` and only
 * lifts `title=` / `file=` into `data-code-title` / `data-filename`, so every
 * key is read from the raw string here.
 */
export function parseFenceMeta(
	props: Record<string, string | undefined>,
): FenceMeta {
	const meta = props['data-meta'] ?? '';
	const language = (
		props['data-language'] ??
		props['data-lang'] ??
		''
	).toLowerCase();

	const attributes = parseAttributes(meta);
	// NOTE: `||` not `??`, so a bare or empty `filename=` still falls through to
	// the parser-provided values instead of blanking the header.
	const filename =
		attributes.filename ||
		attributes.file ||
		attributes.title ||
		props['data-filename'] ||
		props['data-code-title'] ||
		'';

	return {
		filename,
		// NOTE: hand-written blocks (installer tabs) carry `data-icon` on the
		// `code` element instead of `icon=` in a meta string, so an explicit icon
		// prop has to win over the filename lookup or every tab falls back to the
		// language icon.
		icon:
			attributes.icon ||
			props['data-icon'] ||
			(filename ? getFileIcon({ filename, language }) : ''),
		name: attributes.name,
		highlight: parseLineRanges(attributes, meta).join(','),
		language,
	};
}
